import React, { CSSProperties, FC, ReactNode, useEffect, useRef, useState } from 'react'
import { useClickAway } from 'react-use'

import { useMobile } from '@/hooks'

import { BottomSheet } from '..'
import { DropdownButton } from './DropdownButton'

interface Props {
  children: JSX.Element | ReactNode
  className?: string
  disabled?: boolean
  displayedCount?: number
  dropdownButtonStyle?: CSSProperties
  dropdownWrapperClassName?: string
  dropdownWrapperStyle?: CSSProperties
  expanded?: boolean
  fullScreenMobile?: boolean
  headerTitle?: string
  onHideDropdown: () => void
  renderFooter?: () => JSX.Element
  renderHeader?: () => JSX.Element
  renderToggle?: FC
  selected?: boolean
  setExpanded?: (isOpen: boolean) => void
}

export const Dropdown: FC<Props> = ({
  className = '',
  children,
  displayedCount,
  dropdownButtonStyle = {},
  expanded: expandedProp,
  headerTitle = '',
  onHideDropdown,
  renderHeader,
  renderFooter,
  setExpanded: setExpandedProp,
  fullScreenMobile = false,
  selected = false,
  renderToggle: Toggle,
  disabled = false,
  dropdownWrapperClassName,
  dropdownWrapperStyle = {},
}) => {
  const { isMobile } = useMobile()
  const [expanded, setExpanded] = useState(!!expandedProp)
  const dropdownRef = useRef<HTMLDivElement>(null)

  useClickAway(dropdownRef, () => {
    // BottomSheet will dismiss on it's own
    !isMobile && hideDropdown()
  })

  useEffect(() => {
    if (setExpandedProp) setExpandedProp(expanded)
  }, [expanded, setExpandedProp])

  useEffect(() => {
    if (typeof expandedProp === 'boolean') setExpanded(expandedProp)
  }, [expandedProp])

  const handleButtonClick = () => {
    if (!disabled) {
      if (expanded) hideDropdown()
      else setExpanded(true)
    }
  }

  const hideDropdown = () => {
    setExpanded(false)
    if (onHideDropdown) onHideDropdown()
  }

  return (
    <>
      <div className={`relative flex flex-col ${className}`} ref={dropdownRef}>
        {Toggle ? (
          <button onClick={handleButtonClick}>
            <Toggle />
          </button>
        ) : (
          <DropdownButton
            displayedCount={displayedCount}
            expanded={expanded}
            label={headerTitle || ''}
            onClick={handleButtonClick}
            selected={selected}
            style={dropdownButtonStyle}
          />
        )}

        {expanded && !isMobile && (
          <div
            className={`absolute z-20 translate-y-12 rounded-lg border shadow-xl bg-primary border-secondary md:rounded-2xl ${
              dropdownWrapperClassName ? dropdownWrapperClassName : 'right-0'
            }`}
            style={dropdownWrapperStyle}
          >
            {renderHeader ? (
              <div className="w-full">{renderHeader()}</div>
            ) : (
              <>{headerTitle && <div className="w-full px-6 pt-6 font-bold text-header-md">{headerTitle}</div>}</>
            )}

            {children}
            {renderFooter && <div className="w-full border-t border-secondary">{renderFooter()}</div>}
          </div>
        )}
      </div>
      {isMobile && (
        <BottomSheet
          header={headerTitle || ''}
          isVisible={expanded}
          mobileFullHeight={fullScreenMobile}
          onClose={onHideDropdown}
          renderFooter={renderFooter}
          renderHeader={renderHeader}
        >
          {children}
        </BottomSheet>
      )}
    </>
  )
}
